import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import * as MUI from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import ConfirmAndCancelButtons from "../../../../components/buttons/ConfirmAndCancelButtons";
import CountrySelector from "../../../../components/form/CountrySelector";

const FIELDS = [
  { name: "title", label: "ClinicName" },
  { name: "shortName", label: "ShortName" },
  { name: "email", label: "Email" },
  { name: "phoneNumber", label: "PhoneNumber" },
  { name: "streetAddress", label: "Address" },
  { name: "zipCode", label: "ZipCode" },
  { name: "city", label: "City" },
];

const PENDING_FIELDS = FIELDS.filter(({ name }) => name !== "shortName");


function createSchema(t, isPending) {
  const text = (max, isRequired) => {
    const base = z.string().trim().max(max, { message: t("StringToLong") });
    return isRequired ? base.min(1, { message: t("CantBeEmpty") }) : base;
  };
  const zipCode = z
    .string()
    .max(10, { message: t("ZipCodeTooLong") })
    .regex(/^\d*$/, { message: t("InvalidZipCode") });
  const schema = z.object({
    title: text(240, true),
    shortName: text(20, true),
    email: z.union([
      z.literal(""),
      z.string().email({ message: t("InvalidEmail") }),
    ]),
    phoneNumber: text(30, !isPending),
    streetAddress: text(240, !isPending),
    zipCode: isPending ? zipCode : zipCode.min(1, { message: t("CantBeEmpty") }),
    city: text(240, !isPending),
    country: z.string().min(1, { message: t("CantBeEmpty") }),
  });
  return isPending ? schema.omit({ shortName: true }) : schema;
}

function toFormValues(clinic, fields) {
  return {
    ...Object.fromEntries(
      fields.map(({ name }) => [name, clinic?.[name] ? String(clinic[name]) : ""]),
    ),
    country: clinic?.country ?? "",
  };
}


export default function ClinicDetailsDialog({ clinic, onClose, onSave }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const isPending = Boolean(clinic?.isPending);
  const fields = isPending ? PENDING_FIELDS : FIELDS;
  const [isEditing, setIsEditing] = useState(false);
  const isPendingRef = useRef(isPending);
  isPendingRef.current = isPending;
  const methods = useForm({
    resolver: (...args) =>
      zodResolver(createSchema(t, isPendingRef.current))(...args),
  });

  useEffect(() => {
    setIsEditing(isPending);
    methods.reset(toFormValues(clinic, fields));
  }, [clinic]);

  async function handleSave(values) {
    const saved = await onSave({
      ...clinic,
      ...values,
      email: values.email || null,
      zipCode: values.zipCode ? Number(values.zipCode) : null,
    });
    if (saved) setIsEditing(false);
  }

  return (
    <MUI.Dialog
      open={Boolean(clinic)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle>
        <MUI.Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <MUI.Stack direction="row" alignItems="center" spacing={1}>
            {!isEditing && (
              <MUI.Tooltip title={t("Edit")}>
                <MUI.IconButton onClick={() => setIsEditing(true)} size="small">
                  <EditIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            <span>{clinic?.title}</span>
          </MUI.Stack>
          <MUI.IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </MUI.IconButton>
        </MUI.Stack>
      </MUI.DialogTitle>
      <MUI.DialogContent>
        {isEditing ? (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSave)}>
              <MUI.Grid container spacing={2} sx={{ pt: 1 }}>
                {fields.map(({ name, label }) => (
                  <MUI.Grid item xs={12} sm={6} key={name}>
                    <MUI.TextField
                      fullWidth
                      label={t(label)}
                      {...methods.register(name)}
                      error={Boolean(methods.formState.errors[name])}
                      helperText={methods.formState.errors[name]?.message}
                    />
                  </MUI.Grid>
                ))}
                <MUI.Grid item xs={12} sm={6}>
                  <CountrySelector
                    countryName="country"
                    preselectedValue={clinic?.country ?? ""}
                    minWidth={0}
                  />
                </MUI.Grid>
                <MUI.Grid item xs={12}>
                  <ConfirmAndCancelButtons
                    confirmTitle="Save"
                    cancelTitle="Cancel"
                    onCancel={() => {
                      methods.reset(toFormValues(clinic, fields));
                      setIsEditing(false);
                    }}
                    confirmButtonType="submit"
                  />
                </MUI.Grid>
              </MUI.Grid>
            </form>
          </FormProvider>
        ) : (
          <MUI.Grid container spacing={2}>
            {[...fields, { name: "country", label: "Country" }].map(
              ({ name, label }) => (
                <MUI.Grid item xs={12} sm={6} key={name}>
                  <MUI.Typography variant="caption" color="text.secondary">
                    {t(label)}
                  </MUI.Typography>
                  <MUI.Typography>
                    {(name === "country" ? t(clinic?.[name]) : clinic?.[name]) ||
                      "-"}
                  </MUI.Typography>
                </MUI.Grid>
              ),
            )}
          </MUI.Grid>
        )}
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
